import { useEffect, useRef } from 'react';
import { useCountUp } from 'react-countup';

/** 화면에 들어온 순간 지연 없이 세기 시작한다 (라이브러리 기본값은 200ms) */
const SCROLL_SPY_DELAY_MS = 0;

interface CountUpNumberProps {
  end: number;
  start: number;
}

/**
 * 시작값에서 목표값까지 숫자를 세어 올려 보여준다.
 * 화면에 들어올 때 애니메이션이 시작된다.
 */
function CountUpNumber({ end, start }: CountUpNumberProps) {
  if (!end) {
    return start;
  }

  return <AnimatedNumber end={end} start={start} />;
}

/**
 * 세어 올리기 본체. 대상 요소가 화면에 붙은 뒤에만 카운트 인스턴스를 만든다.
 *
 * react-countup 의 CountUp 컴포넌트는 첫 렌더 도중 아직 연결되지 않은 대상으로 인스턴스를
 * 만들어 마운트 1회당 콘솔 에러 1건을 남긴다. 훅 형태는 인스턴스 생성이 마운트 이후라
 * 대상이 항상 준비돼 있다.
 */
function AnimatedNumber({ end, start }: CountUpNumberProps) {
  const targetRef = useRef<HTMLSpanElement>(null);
  const isInitializedRef = useRef(false);

  const { update } = useCountUp({
    ref: targetRef,
    end,
    start,
    enableScrollSpy: true,
    scrollSpyDelay: SCROLL_SPY_DELAY_MS,
  });

  /**
   * 조회 결과가 나중에 갱신되면 새 목표값까지 이어 올린다.
   * 첫 실행은 건너뛴다 — 마운트 시점엔 훅이 이미 세기를 시작한 뒤라, 여기서 또 호출하면
   * countup.js 의 재생시간 절반 최적화가 두 번 걸려 애니메이션이 2배 빨라진다.
   */
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }

    update(end);
  }, [end, update]);

  return <span ref={targetRef}>{start.toLocaleString()}</span>;
}

export default CountUpNumber;
